// src/reports/SalesAnalyticsReport.jsx — Reporting → Store Reports → Backend Reporting → Sales Analytics
// (Eric, 2026-10-18). Replaces the Grafana "Lightspeed Sales Analytics Dashboard" and adds year-over-year:
// the chosen dates against the same dates / same weekdays last year / the period before, for any set of stores
// (none picked = the whole organisation), and week-of-year against week-of-year. Reads OpsHub's Lightspeed tables;
// needs the Extra Permission reports.backend (finance + admin).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, BlockStack, InlineStack, InlineGrid, Text, Banner, Button, Tabs, Spinner, Box, Badge, Tooltip, Select } from '@shopify/polaris';
import { LineChart, BarChart } from '@shopify/polaris-viz';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { withColors, seriesColor } from './chartPalette';
import ColorDonut from './ColorDonut';
import ReportGuide from './ReportGuide';
import { ReportDateRange, ReportPicker, isoDaysAgo, todayIso, downloadCsv, paneHeightToFitScroller } from '../Reporting';

const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined) ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUSD0 = n => (n === null || n === undefined) ? '—' : '$' + Math.round(Number(n)).toLocaleString();
const fmtPct = n => (n === null || n === undefined) ? '—' : `${n > 0 ? '+' : ''}${Number(n).toFixed(1)}%`;
const fmtD = iso => { if (!iso) return ''; const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); };

const COMPARE = [
    { value: 'ly', label: 'Same dates in earlier years' },
    { value: 'ly_dow', label: 'Same weekdays in earlier years (364 days back each)' },
    { value: 'prev', label: 'The periods before' },
    { value: 'none', label: 'No comparison' },
];
const TABS = [
    ['weeks', 'Week of year'], ['stores', 'Store ranking'], ['daily', 'Day by day'], ['byStore', 'Daily revenue by store'],
    ['share', 'Revenue share'], ['txns', 'Transactions by store'], ['avgTxn', 'Avg transaction by store'], ['items', 'Items per transaction'],
];

const COL_HELP = {
    revenue: 'Sale totals (tax included) on completed, non-voided sales.',
    cmp_revenue: 'The same measure over the comparison dates.',
    revenue_change: 'Revenue now − revenue in the comparison dates.',
    revenue_change_pct: '(Revenue now − comparison) ÷ comparison. Blank when the store had no sales in the comparison dates.',
    share_pct: "The store's share of the revenue in view.",
    transactions: 'Completed, non-voided sales (tickets).',
    avg_transaction: 'Revenue ÷ transactions.',
    items_per_txn: 'Merchandise units sold (returns subtract; coupon and discount lines are not items) ÷ transactions.',
    avg_daily_revenue: 'Revenue ÷ days the store had sales.',
    active_days: 'Days the store had sales.',
    week: 'ISO week number (weeks run Monday–Sunday). A part-week is compared with the same weekdays of the same week number last year.',
};

const chgStyle = v => ({ color: v === null || v === undefined || v === 0 ? undefined : (v > 0 ? 'var(--p-color-text-success, #008060)' : 'var(--p-color-text-critical, #d72c0d)'), fontWeight: 600 });

function SortTable({ cols, rows, fit = true, note = null }) {
    const [sort, setSort] = useState({ key: null, dir: 'desc' });
    const wrapRef = useRef(null);
    const [paneH, setPaneH] = useState(null);
    useLayoutEffect(() => {
        if (!fit) return undefined;
        const measure = () => { const el = wrapRef.current; if (!el) return; const h = paneHeightToFitScroller(el); setPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h); };
        measure();
        const t = setTimeout(measure, 300);
        window.addEventListener('resize', measure);
        return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
    }, [fit, rows, paneH]);
    const sorted = useMemo(() => {
        if (!sort.key) return rows;
        const k = sort.key, d = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = a[k], y = b[k];
            if (x === y) return 0;
            if (x === null || x === undefined || x === '') return 1;
            if (y === null || y === undefined || y === '') return -1;
            return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * d;
        });
    }, [rows, sort]);
    const cell = (r, k, kind) => {
        const v = r[k];
        if (kind === 'n') return fmtN(v);
        if (kind === 'u') return fmtUSD(v);
        if (kind === 'u0') return fmtUSD0(v);
        if (kind === 'x') return Number(v || 0).toFixed(2);
        if (kind === 'p') return <span style={chgStyle(v)}>{fmtPct(v)}</span>;
        if (kind === 'q') return v === null || v === undefined ? '—' : Number(v).toFixed(1) + '%';
        if (kind === 'c') return <span style={chgStyle(v)}>{v === null || v === undefined ? '—' : (v > 0 ? '+' : '') + fmtUSD(v)}</span>;
        return v ?? '';
    };
    return (
        <BlockStack gap="200">
            {note && <Text as="p" variant="bodySm" tone="subdued">{note}</Text>}
            <div ref={wrapRef} style={{ overflow: 'auto', maxHeight: fit ? (paneH || undefined) : 460 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--p-color-bg-surface, #fff)', zIndex: 1 }}>
                        <tr>{cols.map(([k, label, kind, help]) => (
                            <th key={k} onClick={() => setSort(s => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}
                                style={{ textAlign: kind === 's' ? 'left' : 'right', padding: '6px 8px', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px solid var(--p-color-border, #ddd)' }}>
                                <Tooltip content={help || COL_HELP[k] || label}><span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span></Tooltip>{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>))}</tr>
                    </thead>
                    <tbody>{sorted.map((r, i) => (
                        <tr key={i}>{cols.map(([k, , kind]) => (
                            <td key={k} style={{ textAlign: kind === 's' ? 'left' : 'right', padding: '5px 8px', borderBottom: '1px solid var(--p-color-border-subdued, #eee)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                {cell(r, k, kind)}</td>))}</tr>))}
                    </tbody>
                </table>
                {rows.length === 0 && <Box padding="400"><Text as="p" tone="subdued" alignment="center">No sales in these dates.</Text></Box>}
            </div>
        </BlockStack>
    );
}

function Stat({ label, value, rows = [], hint }) {
    const body = (
        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
                <Text as="p" variant="headingLg">{value}</Text>
                {rows.map(r => (
                    <InlineStack key={r.label} gap="150" blockAlign="center" wrap={false} align="space-between">
                        <Text as="span" variant="bodySm" tone="subdued">vs {r.label}: {r.value}</Text>
                        {r.pct !== null && r.pct !== undefined && <Badge tone={r.pct > 0 ? 'success' : r.pct < 0 ? 'critical' : undefined}>{fmtPct(r.pct)}</Badge>}
                    </InlineStack>
                ))}
            </BlockStack>
        </Box>);
    return hint ? <Tooltip content={hint}>{body}</Tooltip> : body;
}

// {date, store, <key>} rows -> one polaris-viz series per store, every date in the range (gaps stay gaps = 0 only for money/counts)
function pivot(rows, key, dates, zero) {
    const by = {}, tot = {};
    rows.forEach(r => { (by[r.store] = by[r.store] || {})[r.date] = r[key]; tot[r.store] = (tot[r.store] || 0) + (r.revenue || 0); });
    return withColors(Object.keys(by).sort((a, b) => tot[b] - tot[a]).map(st => ({
        name: st, data: dates.map(d => ({ key: d, value: by[st][d] !== undefined ? by[st][d] : (zero ? 0 : null) })),
    })));
}

const B = ({ children }) => <b>{children}</b>;
const GUIDE = {
    summary: 'How much the stores sell, and how that compares with earlier years: day by day, week by week, and store by store, for the whole company or for any stores you pick.',
    sections: [
        { heading: 'What it is for', items: [
            'Answer "how are we doing?" for any dates, for the whole organisation or specific stores.',
            'Compare the same dates last year (or several years back), the same weekdays, or week-of-year against week-of-year, for example Eatontown\'s last 30 days against Eatontown\'s same 30 days last year.',
            'Rank stores by sales and see which are up or down against the comparison.',
        ] },
        { heading: 'Where the numbers come from', items: [
            "OpsHub's own copy of Lightspeed (our point-of-sale system): every sale, its lines and its payments, back to 2016.",
            'The copy is refreshed every night (1:15 AM). The newest day that is not fully in yet is left out of every period, so each comparison is like for like.',
            'Dates are the store\'s own local date.',
        ] },
        { heading: 'The boxes', items: [
            <><B>Net sales</B>: what customers paid for goods, with sales tax taken out and returns subtracted. Switch the Revenue selector to "Total collected" to include tax.</>,
            <><B>Transactions</B>: sales that took money. Gift cards being loaded ($0 tickets) and return tickets are not counted.</>,
            <><B>Average transaction value</B>: sales from those transactions (before returns) divided by the number of transactions.</>,
            <><B>Items per transaction</B>: units sold (returns subtract) divided by transactions. Coupon and discount lines are not items. Bulk lines such as 120 sticker packs on one ticket can push a single day up.</>,
            <><B>Active stores</B>: stores with sales in the dates. <B>Gift cards sold</B>: money loaded onto gift cards at the registers (cards the Team Portal issues in bulk are not counted). It is not sales revenue; the money is earned when a card is spent, and that spending is already in Net sales. <B>Returns</B>: dollars and tickets of returns, already netted out of Net sales.</>,
        ] },
        { heading: 'Comparing periods', items: [
            <><B>Same dates in earlier years</B> lines up calendar dates. <B>Same weekdays</B> goes back 364 days per year so a Saturday lines up with a Saturday. <B>The periods before</B> steps back by the length of your range.</>,
            <><B>Years back</B> (1 to 10) adds one line, bar and column per year. Each Δ% compares this period with that one year, never year to year; hover a column title to see exactly which.</>,
            <><B>Week of year</B> uses ISO weeks (Monday to Sunday). A part-week at either end of your dates is compared with the same weekdays of the same week.</>,
        ] },
        { heading: 'How to use it', items: [
            'Pick dates, stores (blank = the whole organisation), what to compare to and how far back, then Run Report. The address in your browser holds your choices.',
            'Tabs: week of year, store ranking, day by day, and per-store charts (daily revenue, revenue share, transactions, average transaction, items per transaction).',
            'Click a column title to sort; hover for its meaning; Export CSV saves the table you are viewing.',
        ] },
    ],
};

export default function SalesAnalyticsReport() {
    const fetchAuth = useAuthFetch();
    const [sp, setSp] = useSearchParams();
    const [dateFrom, setDateFrom] = useState(sp.get('from') || isoDaysAgo(29));
    const [dateTo, setDateTo] = useState(sp.get('to') || todayIso());
    const [stores, setStores] = useState(sp.getAll('stores'));
    const [compare, setCompare] = useState(sp.get('compare') || 'ly');
    const [years, setYears] = useState(sp.get('years') || '1');
    const [basis, setBasis] = useState(sp.get('basis') || 'net');
    const [tab, setTabState] = useState(Math.max(0, TABS.findIndex(t => t[0] === sp.get('tab'))));
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        document.body.classList.add('report-flush-bottom');
        return () => document.body.classList.remove('report-flush-bottom');
    }, []);

    const writeUrl = useCallback((f, tabIdx) => {
        setSp(prev => {
            const n = new URLSearchParams(prev);
            ['from', 'to', 'stores', 'compare', 'years', 'basis', 'tab'].forEach(k => n.delete(k));
            n.set('from', f.dateFrom); n.set('to', f.dateTo); n.set('compare', f.compare); if (String(f.years) !== '1') n.set('years', String(f.years));
            if (f.basis !== 'net') n.set('basis', f.basis);
            f.stores.forEach(v => n.append('stores', v));
            if (tabIdx > 0) n.set('tab', TABS[tabIdx][0]);
            return n;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSp]);
    const setTab = (i) => { setTabState(i); writeUrl({ dateFrom, dateTo, stores, compare, years, basis }, i); };

    const run = useCallback(async () => {
        setBusy(true); setErr(null);
        writeUrl({ dateFrom, dateTo, stores, compare, years, basis }, tab);
        try {
            const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, compare, years, basis });
            stores.forEach(v => p.append('stores', v));
            const r = await fetchAuth(`/api/reports/backend/sales-analytics?${p}`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
    }, [fetchAuth, dateFrom, dateTo, stores, compare, years, basis, tab, writeUrl]);
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const storeOptions = useMemo(() => (data?.store_options || []).map(v => ({ value: v, label: v })), [data]);
    const dates = useMemo(() => (data?.daily || []).map(r => r.date), [data]);
    const cmps = data?.compares || [];
    const hasCmp = cmps.length > 0;
    const multi = cmps.length > 1;
    const cmpLabel = cmps.length === 0 ? '' : cmps.length === 1 ? `${fmtD(cmps[0].from)} – ${fmtD(cmps[0].to)}` : `${cmps.map(c => c.label).join(', ')}`;
    const scope = stores.length === 0 ? 'the whole organisation' : stores.length === 1 ? stores[0] : `${stores.length} stores`;
    const thisLabel = data ? (data.date_from.slice(0, 4) === data.date_to.slice(0, 4) ? data.date_from.slice(0, 4) : `${data.date_from.slice(0, 4)}-${data.date_to.slice(2, 4)}`) : '';

    // one line per year, laid over this period day for day (a single comparison is dashed)
    const dailyLine = useMemo(() => {
        if (!data) return [];
        const out = [{ name: `This period (${thisLabel})`, data: data.daily.map(r => ({ key: r.date, value: r.revenue })) }];
        cmps.forEach((c, i) => out.push({ name: c.label, ...(cmps.length === 1 ? { isComparison: true } : {}), data: data.daily.map(r => ({ key: r.date, value: r.cmp[i].revenue })) }));
        return withColors(out);
    }, [data, cmps, thisLabel]);
    const weekBars = useMemo(() => {
        if (!data) return [];
        const out = [{ name: thisLabel, data: data.weeks.map(w => ({ key: `W${w.week}`, value: w.revenue })) }];
        cmps.forEach((c, i) => out.push({ name: c.label, data: data.weeks.map(w => ({ key: `W${w.week}`, value: w.cmp[i]?.revenue ?? 0 })) }));
        return withColors(out);
    }, [data, cmps, thisLabel]);
    const byStore = useMemo(() => data?.daily_by_store || [], [data]);
    const lines = useMemo(() => ({
        byStore: pivot(byStore, 'revenue', dates, true), txns: pivot(byStore, 'transactions', dates, true),
        avgTxn: pivot(byStore, 'avg_transaction', dates, false), items: pivot(byStore, 'items_per_txn', dates, false),
    }), [byStore, dates]);
    const donut = useMemo(() => (data?.stores || []).map((r, i) => ({ name: r.store, color: seriesColor(i), data: [{ key: 'revenue', value: r.revenue }] })), [data]);

    const S = data?.summary;
    const key = TABS[tab][0];

    // flatten the per-year lists into columns (rev_1, chg_1 ...) so the sortable table and the CSV can use them
    const flat = (rows, fields) => (rows || []).map(r => {
        const o = { ...r };
        cmps.forEach((c, i) => { const x = r.cmp?.[i]; Object.entries(fields).forEach(([to, from]) => { o[`${to}_${i}`] = x ? x[from] : null; }); });
        return o;
    });
    // header tooltips that say exactly which period a change is measured against
    const hRev = c => `Revenue in ${c.label}: ${fmtD(c.from)} – ${fmtD(c.to)}.`;
    const hPct = c => `Change % = (${thisLabel} − ${c.label}) ÷ ${c.label}, compared with ${c.label} (${fmtD(c.from)} – ${fmtD(c.to)}), not the other years. Positive means ${thisLabel} is higher.`;
    const hTxn = c => `Transactions in ${c.label}.`;
    const hTpct = c => `Transaction change % = (${thisLabel} − ${c.label}) ÷ ${c.label}, compared with ${c.label} only.`;
    const yearCols = (revLabel, withChg = true) => cmps.flatMap((c, i) => [
        [`rev_${i}`, multi ? `${c.label}` : revLabel, 'u0', hRev(c)], ...(withChg ? [[`pct_${i}`, `Δ% vs ${c.label}`, 'p', hPct(c)]] : []),
    ]);
    const weekRows = useMemo(() => flat(data?.weeks, { rev: 'revenue', pct: 'revenue_change_pct', chg: 'revenue_change', txn: 'transactions', tpct: 'transactions_change_pct' }), [data]); // eslint-disable-line
    const storeRows = useMemo(() => flat(data?.stores, { rev: 'revenue', pct: 'revenue_change_pct', chg: 'revenue_change', txn: 'transactions', tpct: 'transactions_change_pct' }), [data]); // eslint-disable-line
    const dayRows = useMemo(() => (data?.daily || []).map(r => {
        const o = { ...r };
        cmps.forEach((c, i) => {
            const x = r.cmp[i];
            o[`date_${i}`] = x.date; o[`rev_${i}`] = x.revenue; o[`txn_${i}`] = x.transactions;
            o[`pct_${i}`] = x.revenue ? Math.round((r.revenue - x.revenue) / x.revenue * 1000) / 10 : null;
        });
        return o;
    }), [data]); // eslint-disable-line

    const weekCols = [['week', 'Week', 's'], ['start', 'This year from', 's'], ['end', 'to', 's'], ['days', 'Days', 'n'], ['revenue', thisLabel || 'Revenue', 'u0'],
        ...yearCols('Last year'), ['transactions', 'Transactions', 'n'],
        ...cmps.flatMap((c, i) => [[`txn_${i}`, multi ? `${c.label} txns` : 'Last year txns', 'n', hTxn(c)], [`tpct_${i}`, `Txn Δ% vs ${c.label}`, 'p', hTpct(c)]]),
        ['avg_transaction', 'Avg transaction', 'u']];
    const storeCols = [['rank', 'Rank', 'n'], ['store', 'Store', 's'], ['revenue', thisLabel ? `Revenue ${thisLabel}` : 'Revenue', 'u0'],
        ...yearCols('Comparison revenue'), ['share_pct', 'Share', 'q'], ['transactions', 'Transactions', 'n'],
        ...cmps.map((c, i) => [`tpct_${i}`, `Txn Δ% vs ${c.label}`, 'p', hTpct(c)]),
        ['avg_transaction', 'Avg transaction', 'u'], ['items_per_txn', 'Items / txn', 'x'], ['avg_daily_revenue', 'Avg daily revenue', 'u0'], ['active_days', 'Active days', 'n']];
    const dayCols = [['date', 'Date', 's'], ['revenue', thisLabel || 'Revenue', 'u0'],
        ...cmps.flatMap((c, i) => [[`date_${i}`, multi ? `${c.label} date` : 'Comparison date', 's', `The day in ${c.label} that lines up with this row's date.`], [`rev_${i}`, multi ? c.label : 'Comparison revenue', 'u0', hRev(c)], [`pct_${i}`, `Δ% vs ${c.label}`, 'p', hPct(c)]]),
        ['transactions', 'Transactions', 'n']];
    const tableFor = { weeks: [weekCols, weekRows], stores: [storeCols, storeRows], daily: [dayCols, dayRows] };
    const exportCsv = () => {
        const t = tableFor[key];
        if (!data || !t) return;
        downloadCsv(`sales-analytics-${key}-${dateFrom}-${dateTo}.csv`, [t[0].map(c => c[1]), ...(t[1] || []).map(r => t[0].map(c => r[c[0]] ?? ''))]);
    };

    const money = { yAxisOptions: { labelFormatter: v => '$' + Number(v).toLocaleString() }, tooltipOptions: { valueFormatter: fmtUSD } };

    return (
        <BlockStack gap="300">
            {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
            <ReportGuide id="sales-analytics" summary={GUIDE.summary} sections={GUIDE.sections} />
            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                        <ReportPicker label="Store" options={storeOptions} value={stores} onChange={setStores} multiple searchable placeholder="All stores" />
                        <div style={{ minWidth: 300 }}><Select label="Compare to" labelInline options={COMPARE} value={compare} onChange={setCompare} /></div>
                        {compare !== 'none' && (
                            <div style={{ minWidth: 190 }}>
                                <Select label={compare === 'prev' ? 'Periods back' : 'Years back'} labelInline value={String(years)} onChange={setYears}
                                    options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: i === 0 ? '1 (last year)' : String(i + 1) }))} />
                            </div>
                        )}
                        <div style={{ minWidth: 270 }}>
                            <Select label="Revenue" labelInline value={basis} onChange={setBasis}
                                options={[{ value: 'net', label: 'Net sales (tax excluded)' }, { value: 'total', label: 'Total collected (tax included)' }]} />
                        </div>
                        <Button variant="primary" onClick={run} loading={busy}>Run Report</Button>
                        <Button onClick={exportCsv} disabled={!data || !tableFor[key]}>Export CSV</Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                        {data ? <>Showing <b>{scope}</b>, {fmtD(data.date_from)} – {fmtD(data.date_to)}{hasCmp ? <>, compared with <b>{cmpLabel}</b></> : ''}. </> : ''}
                        {basis === 'net'
                            ? "Revenue is net sales: the sale total minus sales tax, on completed, non-voided Lightspeed sales, with returns netted, by the store's local date."
                            : "Revenue is the sale total (tax included) on completed, non-voided Lightspeed sales, with returns netted, by the store's local date."}
                        {' '}Transactions are sales that took money; $0 tickets (mostly gift cards being loaded) and returns are counted separately.
                        {data?.synced_through ? ` Sales load nightly, through ${String(data.synced_through).slice(0, 16).replace('T', ' ')}.` : ''}
                        {data && data.requested_to > data.date_to ? ` Days after ${fmtD(data.date_to)} are not loaded yet, so every period stops at the same day.` : ''}
                    </Text>
                </BlockStack>
            </Card>

            {busy && !data && <InlineStack align="center"><Spinner /></InlineStack>}
            {S && (() => {
                const tile = (label, cur, fmt, m, hint, rowFmt = fmt) => (
                    <Stat label={label} value={fmt(cur)} hint={hint}
                        rows={S.compares.map(c => ({ label: c.label, value: rowFmt(c.totals[m]), pct: c.change_pct[m] }))} />
                );
                return (
                    <InlineGrid columns={{ xs: 2, sm: 3, lg: 4 }} gap="200">
                        {tile(basis === 'net' ? 'Net sales' : 'Total revenue', S.current.revenue, fmtUSD, 'revenue',
                            basis === 'net' ? 'Sale totals minus sales tax, returns netted.' : 'Sale totals including sales tax, returns netted.', fmtUSD0)}
                        {tile('Transactions', S.current.transactions, fmtN, 'transactions', 'Sales that took money (total above $0). $0 tickets (mostly gift cards being loaded) and returns are not counted here.')}
                        {tile('Average transaction value', S.current.avg_transaction, fmtUSD, 'avg_transaction', 'Revenue from sales (before returns) ÷ transactions.')}
                        {tile('Items per transaction', S.current.items_per_txn, v => Number(v).toFixed(2), 'items_per_txn', 'Merchandise units sold (returns subtract; coupon and discount lines are not items) ÷ transactions.')}
                        {tile('Active stores', S.current.active_stores, fmtN, 'active_stores', 'Stores with sales in the dates.')}
                        {tile('Gift cards sold', S.current.gift_cards_sold, fmtUSD0, 'gift_cards_sold', `${fmtN(S.current.gift_cards)} cards loaded. Not revenue: the money is earned when a card is spent, and that spending is already in revenue. Cards the Team Portal issues in bulk are not counted.`)}
                        {tile('Returns', S.current.returns_amount, fmtUSD0, 'returns_amount', `${fmtN(S.current.returns)} return tickets, already netted out of revenue and not counted as transactions.`)}
                    </InlineGrid>
                );
            })()}

            {data && (
                <Card>
                    <BlockStack gap="200">
                        <Text as="h2" variant="headingMd">Daily revenue{hasCmp ? (multi ? ' — this period against earlier years' : ' — this period against the comparison') : ''}</Text>
                        {hasCmp && <Text as="p" variant="bodySm" tone="subdued">{multi ? 'One line per year, each laid over this period day for day.' : `The dashed line is ${cmpLabel}, laid over this period day for day.`}</Text>}
                        <div style={{ height: 320 }}><LineChart data={dailyLine} {...money} /></div>
                    </BlockStack>
                </Card>
            )}

            {data && (
                <Card padding="0">
                    <Tabs tabs={TABS.map(([id, content]) => ({ id, content }))} selected={tab} onSelect={setTab} />
                    <Box padding="300" paddingBlockEnd="0">
                        {key === 'weeks' && (
                            <BlockStack gap="300">
                                <div style={{ height: 240 }}><BarChart data={weekBars} yAxisOptions={money.yAxisOptions} tooltipOptions={money.tooltipOptions} /></div>
                                <SortTable key="weeks" cols={weekCols} rows={weekRows}
                                    note="Week N this year against week N in earlier years (ISO weeks, Monday–Sunday). A part-week at either end of the dates is compared with the same weekdays of that week." />
                            </BlockStack>
                        )}
                        {key === 'stores' && <SortTable key="stores" cols={storeCols} rows={storeRows} />}
                        {key === 'daily' && <SortTable key="daily" cols={dayCols} rows={dayRows} />}
                        {key === 'byStore' && <div style={{ height: 420 }}><LineChart data={lines.byStore} {...money} /></div>}
                        {key === 'share' && <div style={{ height: 420 }}><ColorDonut data={donut} format={fmtUSD} height={420} /></div>}
                        {key === 'txns' && <div style={{ height: 420 }}><LineChart data={lines.txns} /></div>}
                        {key === 'avgTxn' && <div style={{ height: 420 }}><LineChart data={lines.avgTxn} {...money} /></div>}
                        {key === 'items' && <div style={{ height: 420 }}><LineChart data={lines.items} /></div>}
                    </Box>
                </Card>
            )}
        </BlockStack>
    );
}
